import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { FOODIE_ACCESS_MODEL } from '../Model/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import { toast } from 'react-toastify';
import { GetAllCafeterias } from "../../../../../reduxToolkit/FoodieShift/FoodieShiftApi";
import { DATE_TIME_FORMAT_MODEL, errorMsg, isEndTimeAfterStartTime } from "../../../../../Apis/functions";
import { CreateAccessCode } from "../../../../../reduxToolkit/FoodieAccessCode/FoodieAccessCodeApi";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * Manages state and logic for creating a new access code, including form state,
 * validation, submission, and fetching related data (cafeterias, users).
 *
 * @param {Object} props - Hook properties
 * @param {Function} props.onHide - Callback to close the modal
 * @param {Function} props.setModified - Callback to update parent component when a new access code is created
 * @returns {Object} State and functions for AddAccessCode component
 */
const AddAccessCodeApp = ({ onHide, setModified }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const cafeteriasOptions = useSelector(state => state.FoodieShiftSlice)?.allCafeterias;
    const userId = useSelector((state) => state.authenticatioauthennSlice.getUserByIdAtEntry.userId);
    const [item, setItem] = useState({ ...FOODIE_ACCESS_MODEL, requestedById: userId });
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [disabled, setDisabled] = useState(false);
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValidTime, setIsValidTime] = useState(true);
    const [selectedList, setSelectedList] = useState([]);

    /**
     * Fetch cafeterias and users on mount
     */
    useEffect(() => {
        dispatch(GetAllCafeterias());
    }, []);

    /**
     * Validate time range whenever fromTime or toTime changes
     */
    useEffect(() => {
        if (item?.fromTime && item?.toTime) {
            setIsValidTime(isEndTimeAfterStartTime(item?.fromTime, item?.toTime, DATE_TIME_FORMAT_MODEL.en.dateTime24Hour));
        }
    }, [item?.fromTime, item?.toTime]);

    /**
     * Handle form submission to create a new access code
     */
    const handleSubmit = () => {
        const isValid = item.isValid() && isValidTime && selectedList.length > 0

        if (!isValid) {
            setEmptyFields(true);
            return;
        }

        let body = item.toRegister(selectedList[0].userId);

        dispatch(CreateAccessCode(body)).then(({ payload: { data: { data, code } } }) => {
            if (data?.id) {
                toast.success(t('has_been_created_successfully'));
                setModified(prev => prev + 1);
                onHide();
            } else {
                errorMsg(code);
            }
        });
    };

    /**
     * Clear all form fields and reset state
     */
    const handleClearFields = () => {
        setItem({ ...FOODIE_ACCESS_MODEL, requestedById: userId });
        setSelectedList([]);
        setEmptyFields(false);
        setIsValidTime(true);
    };


    return {
        t,
        disabled,
        emptyFields,
        item,
        isValidTime,
        handleChange,
        handleSubmit,
        handleClearFields,
        selectedList,
        setSelectedList,
        cafeteriasOptions
    };
};

export default AddAccessCodeApp;
